import { useWebgate } from "@xvia/webgate-connect";
import { FC, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getTopCatalog } from "../../apis/relevant-catalog";
import { Catalog } from "../../apis/search-catalog";
import { getServerData } from "../../util/get-server-data";
import { RelevantServicesProps, TopServices } from "./top-services";

export interface TopServicesContainerProps {
  allCatalogList?: Catalog[];
  favoriteList?: string[];
  onFavoriteUpdate?: (slug: string, remove: boolean) => void;
  setMessageAlert?: (message: string) => void;
  setSeverityAlert?: (severity: "success" | "error") => void;
  setOpenAlert?: (open: boolean) => void;
  codIbgeMunicipio?: number;
  municipalityName?: string;
  municipioQuerySuffix?: string;
  isMunicipalityHome?: boolean;
}

function initialTopList(
  codIbgeMunicipio: number | undefined,
  serverData: { top?: Catalog[] } | undefined
): Catalog[] {
  if (codIbgeMunicipio) {
    return [];
  }
  const raw = serverData?.top ?? getServerData("top", null);
  return Array.isArray(raw) ? raw : [];
}

export const TopServicesContainer: FC<TopServicesContainerProps> = ({
  favoriteList,
  onFavoriteUpdate,
  setMessageAlert,
  setSeverityAlert,
  setOpenAlert,
  codIbgeMunicipio,
  municipalityName,
  municipioQuerySuffix = "",
  isMunicipalityHome,
}) => {
  const { accessToken, serverData } = useWebgate();
  let history = useHistory();

  const [relevantCatalogList, setRelevantCatalogList] = useState<Catalog[]>(
    () =>
      isMunicipalityHome && !codIbgeMunicipio
        ? []
        : initialTopList(codIbgeMunicipio, serverData)
  );

  const [hasFetched, setHasFetched] = useState(() => {
    if (isMunicipalityHome && !codIbgeMunicipio) {
      return true;
    }
    if (codIbgeMunicipio) {
      return false;
    }
    const seeded = initialTopList(undefined, serverData);
    return seeded.length > 0;
  });

  const getServerRelevant = useCallback(async () => {
    if (isMunicipalityHome && !codIbgeMunicipio) {
      setRelevantCatalogList([]);
      setHasFetched(true);
      return;
    }

    try {
      const result = await getTopCatalog(accessToken, codIbgeMunicipio);
      setRelevantCatalogList(result ?? []);
    } catch (err) {
      console.log(err);
      setRelevantCatalogList([]);
    } finally {
      setHasFetched(true);
    }
  }, [accessToken, codIbgeMunicipio, isMunicipalityHome]);

  const onClickCard = useCallback(
    (categorySlug, slug) => {
      history.push(`/${categorySlug}/${slug}${municipioQuerySuffix}`);
    },
    [history, municipioQuerySuffix]
  );

  const handleFavoriteClick = useCallback(
    (slug: string, isFavorite: boolean) => {
      onFavoriteUpdate?.(slug, isFavorite);
    },
    [onFavoriteUpdate]
  );

  useEffect(() => {
    if (isMunicipalityHome && !codIbgeMunicipio) {
      setRelevantCatalogList([]);
      setHasFetched(true);
      return;
    }

    if (codIbgeMunicipio) {
      void getServerRelevant();
      return;
    }
    if (relevantCatalogList?.length) {
      setHasFetched(true);
    } else {
      void getServerRelevant();
    }
  }, [
    codIbgeMunicipio,
    getServerRelevant,
    isMunicipalityHome,
    relevantCatalogList?.length,
  ]);

  if (!hasFetched || !relevantCatalogList?.length) {
    return null;
  }

  const relevantServicesProps: RelevantServicesProps = {
    title: municipalityName
      ? `Mais acessados - ${municipalityName}`
      : "Mais acessados",
    linkUrl: `/app/catalog/search/mais-acessados${municipioQuerySuffix}`,
    services: relevantCatalogList.map((catalog) => {
      const isFavorite = favoriteList?.includes(catalog.slug);
      return {
        title: catalog.title,
        description: catalog.description,
        categoryIcon: catalog.icon,
        onClick: () => {
          onClickCard(catalog.categorySlug, catalog.slug);
        },
        imageUrl: catalog.imageUrl,
        onClickFavourite: () => {},
        type: catalog.isOnline ? "online" : "in-person",
        targets: catalog?.targets,
        accessProfile: catalog?.accessProfile,
        slug: catalog.slug,
        setSeverityAlert,
        setOpenAlert,
        setMessageAlert,
        onClickFavorite: () =>
          handleFavoriteClick(catalog.slug, isFavorite ?? false),
        isFavorite,
        municipalityName: municipalityName,
      };
    }),
  };

  return <TopServices {...relevantServicesProps} />;
};
